import React, { useState } from "react";
import NumberFormat from "react-number-format";

import { Autocomplete, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { capitalize } from "lodash";
import { useManagersSelect } from "store";
import { AccountSettings, AccountSettings as IAccountSettings } from "store/types";

interface State extends Omit<IAccountSettings, "validation_settings"> {
  periodicLockEnabled: boolean;
  debtReminderEnabled: boolean;
}

const defaultState: State = {
  periodicLockEnabled: false,
  debtReminderEnabled: false,
  lock_day: null,
  lock_frequency: "monthly",
  debt_reminder_days: null,
  debt_reminder_users: null
};

interface Props {
  initialState: State;
  updating: boolean;
  updateSettings: (data: Partial<AccountSettings>) => Promise<AccountSettings>;
}

export const DebtSettings: React.FC<Props> = ({ initialState = defaultState, updating, updateSettings }) => {
  const managers = useManagersSelect();

  const [state, setState] = useState<State>(initialState);

  const handleFieldChange = async (field: keyof State, value: State[keyof State]) => {
    setState((prev) => ({
      ...prev,
      [field]: value
    }));

    // When toggling checkboxes, clear or set the related fields
    if (field === "periodicLockEnabled") {
      if (value) {
        // Enable: send current values
        await updateSettings({
          lock_day: state.lock_day,
          lock_frequency: state.lock_frequency
        });
      } else {
        // Disable: clear the fields
        await updateSettings({
          lock_day: null,
          lock_frequency: state.lock_frequency
        });
      }
    } else if (field === "debtReminderEnabled") {
      if (value) {
        // Enable: send current values
        await updateSettings({
          debt_reminder_days: state.debt_reminder_days,
          debt_reminder_users: state.debt_reminder_users
        });
      } else {
        // Disable: clear the fields
        await updateSettings({
          debt_reminder_days: null,
          debt_reminder_users: null
        });
      }
    } else {
      // For regular field changes, only send if the section is enabled
      if (field === "lock_day" || field === "lock_frequency") {
        if (!state.periodicLockEnabled) return;

        await updateSettings({
          lock_day: field === "lock_day" ? (value as number) : state.lock_day,
          lock_frequency: (field === "lock_frequency"
            ? value
            : state.lock_frequency) as IAccountSettings["lock_frequency"]
        });
      } else if (field === "debt_reminder_days" || field === "debt_reminder_users") {
        if (!state.debtReminderEnabled) return;

        await updateSettings({
          debt_reminder_days: field === "debt_reminder_days" ? (value as number) : state.debt_reminder_days,
          debt_reminder_users: field === "debt_reminder_users" ? (value as number[]) : state.debt_reminder_users
        });
      }
    }
  };

  return (
    <Stack
      sx={{
        gap: "16px",
        bgcolor: "#FFFFFF",
        p: "16px",
        boxShadow: "sm",
        borderRadius: "8px"
      }}
    >
      <Typography sx={{ color: "#071F35" }} variant="h6">
        Debt settings
      </Typography>
      <Stack sx={{ flexDirection: "row", gap: "20px" }}>
        {/* Periodic lock measure data section */}
        <Stack sx={{ flex: "50%" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={state.periodicLockEnabled}
                onChange={(e) => handleFieldChange("periodicLockEnabled", e.target.checked)}
                disabled={updating}
              />
            }
            label={<Typography variant="body1">Periodic Lock Measure Data</Typography>}
          />

          <Stack sx={{ marginLeft: "32px", gap: "10px" }}>
            <TextField
              disabled={!state.periodicLockEnabled || updating}
              label="Lock Days After Period End"
              value={state.lock_day}
              select
              onChange={(e) => handleFieldChange("lock_day", Number(e.target.value))}
            >
              {Array.from({ length: 32 }, (_, i) => i).map((day) => (
                <MenuItem key={day} value={day}>
                  {day === 0 ? `${day} (End of period)` : day}
                </MenuItem>
              ))}
            </TextField>
            <Autocomplete
              disabled={!state.periodicLockEnabled || updating}
              value={state.lock_frequency}
              options={["monthly", "quarterly", "yearly"]}
              onChange={(_, value) => handleFieldChange("lock_frequency", value as IAccountSettings["lock_frequency"])}
              renderInput={(params) => <TextField {...params} label="Lock Frequency" />}
              renderOption={(props, option) => <li {...props}>{capitalize(option)}</li>}
              getOptionLabel={(option) => capitalize(option)}
            />
          </Stack>
        </Stack>

        {/* Debt payment reminder section */}
        <Stack sx={{ flex: "50%" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={state.debtReminderEnabled}
                onChange={(e) => handleFieldChange("debtReminderEnabled", e.target.checked)}
                disabled={updating}
              />
            }
            label={<Typography variant="body1">Debt Payment Reminder</Typography>}
          />

          <Stack sx={{ marginLeft: "32px", gap: "10px" }}>
            <NumberFormat
              disabled={!state.debtReminderEnabled || updating}
              label="Days Prior To Expected Payment"
              customInput={TextField}
              value={state.debt_reminder_days}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                handleFieldChange("debt_reminder_days", Number(e.target.value))
              }
            />

            <Autocomplete
              disabled={!state.debtReminderEnabled || updating}
              multiple
              value={state.debt_reminder_users || []}
              getOptionLabel={(option) => managers.find((user) => user.id === option)?.full_name || ""}
              options={managers.map((user) => user.id)}
              onChange={(_, value) => handleFieldChange("debt_reminder_users", value || [])}
              renderInput={(params) => <TextField {...params} label="Email Users" />}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
